import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs';

import { isEmpty } from '@app/util/util';
import { LoaderRingType } from '@design-system/components/atoms/loader-ring/entities/loader-ring.types';
import { SearchType } from '@search-bar/entities/search-type';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchBarMobileService } from '@search-bar/services/search-bar-mobile.service';
import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import {
  AutoCompleteDestination, AutoCompleteDestinationHotel, AutoCompleteDestinationLocation,
  HotelDestinationInputType, InputDestinationPlace, LocationType
} from '@shared/base-search-bar/entities/autocomplete-destionation';
import { EventProperties } from '@avo/interface/trigger-props';
import { AutocompletePlaces } from '@search-bar/entities/autocomplete-places';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize } from '@design-system/components/molecules/button-icon/entities/button-icon.types';

@Component({
  selector: 'app-packages-destination-abstract',
  template: '',
  standalone: false
})
export class PackagesDestinationAbstractComponent {

  @Input() isOneWayTrip = false;
  @Input() isLoadingAutocompletePlaces = false;
  @Input() searchType: SearchType;
  @Input() cityAndAirportsPlaces: AutocompletePlaces;
  @Input() hotelOrLocationPlaces: AutoCompleteDestination;
  @Input() departurePlace: InputDestinationPlace;
  @Input() destinationPlace: InputDestinationPlace;
  @Input() isSubmited = false;
  @Input() isVertSwipe = false;

  @Output() selectPackagesDestination: EventEmitter<InputDestinationPlace> = new EventEmitter<InputDestinationPlace>();
  @Output() selectPackagesDeparture: EventEmitter<InputDestinationPlace> = new EventEmitter<InputDestinationPlace>();
  @Output() popularAmplitudeEvent: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();
  @Output() runAutocomplete: EventEmitter<{ q: string, type: PlaceType }> = new EventEmitter<{ q: string, type: PlaceType }>();
  @Output() resetAutocomplete: EventEmitter<void> = new EventEmitter<void>();
  @Output() swipeAction: EventEmitter<void> = new EventEmitter<void>();

  public readonly loaderRingType = LoaderRingType.Neutral;

  public readonly buttonIconSize = ButtonIconSize.LG;

  public viewAutocomplete = false;
  public isDestinationSelected = false;
  public query = '';

  protected emitAutocomplete$: Subject<string>;
  protected subscription$ = new Subject<void>();

  constructor(
    protected searchBarMobileService: SearchBarMobileService
  ) { }

  get cityAndAirportsAutocomplete(): AutocompletePlaces {
    return { airports: this.cityAndAirportsPlaces?.airports ?? [], cities: this.cityAndAirportsPlaces?.cities ?? [] };
  }

  get hotelAndLocationsAutocomplete(): AutoCompleteDestination {
    return { hotels: this.hotelOrLocationPlaces?.hotels ?? [], locations: this.hotelOrLocationPlaces?.locations ?? [] };
  }

  get arrowIcon(): Icon {
    return this.searchBarMobileService.arrowIcon(this.isOneWayTrip, this.isVertSwipe);
  }

  get placeType(): PlaceType {
    return this.isDestinationSelected ? PlaceType.ARRIVAL : PlaceType.DEPARTURE;
  }

  get destinationInputValue(): string {
    return this.destinationPlace?.InputType === HotelDestinationInputType.location ?
      this.destinationPlace?.NameFull : this.destinationPlace?.hotel_name;
  }

  get destinationInputHasAnError(): boolean {
    return (this.isEmptyDestinationPlace && this.isSubmited) || this.isTheSamePlaceByIataCode;
  }

  get destinationInputError(): string {
    return this.isTheSamePlaceByIataCode ? 'SEARCH_MODULE.PLACE_TOOLTIP.ARRIVAL' :
      this.isEmptyDestinationPlace ? 'SEARCH_MODULE.LOCATION_SEARCH.SELECT_DESTINATION_ERROR' : '';
  }

  get departureInputHasAnError(): boolean {
    return this.isEmptyDeparturePlace && this.isSubmited;
  }

  private get isEmptyDeparturePlace(): boolean {
    return isEmpty(this.departurePlace);
  }

  private get isEmptyDestinationPlace(): boolean {
    return isEmpty(this.destinationPlace);
  }

  private get isTheSamePlaceByIataCode(): boolean {
    return !this.isEmptyDestinationPlace && !this.isEmptyDeparturePlace ?
      this.destinationPlace.NearestIata === this.departurePlace.NearestIata : false;
  }

  public toggleAutocompleteView(isDestinationAction: boolean): void {
    this.viewAutocomplete = !this.viewAutocomplete;
    this.isDestinationSelected = isDestinationAction;
    if (this.viewAutocomplete) {
      this.initKeyEvent();
    } else {
      this.emitAutocomplete$?.unsubscribe();
    }
  }

  public close(): void {
    this.query = '';
    this.toggleAutocompleteView(this.isDestinationSelected);
    this.resetAutocomplete.emit();
  }

  public setQuery(value: string): void {
    this.query = value;
    if (!isEmpty(this.query)) {
      this.emitAutocomplete$?.next(this.query);
    } else {
      this.resetAutocomplete.emit();
    }
  }

  public selectAirportOrCity(data: { place: AutocompletePlace, isAirport: boolean }): void {
    const selectedPlace = {
      Id: 0,
      Name: data?.place.description,
      NameFull: `${data?.place.description}, ${data?.place.country}`,
      Type: data?.isAirport ? LocationType.Airport : null,
      NearestIata: data?.place.code,
      InputType: HotelDestinationInputType.location,
      isTravelCitySearch: true,
    };
    this.departurePlace = selectedPlace;
    this.selectPackagesDeparture.emit(selectedPlace);
    this.close();
  }

  public selectLocation(place: AutoCompleteDestinationLocation): void {
    const selectedPlace = {
      Id: place?.Id,
      Name: place?.Name,
      NameFull: place?.NameFull,
      Type: place?.Type,
      NearestIata: place?.nearestIataCode,
      InputType: HotelDestinationInputType.location,
      isTravelCitySearch: false,
    };
    this.destinationPlace = selectedPlace;
    this.selectPackagesDestination.emit(selectedPlace);
    this.close();
  }

  public selectHotel(place: AutoCompleteDestinationHotel): void {
    const selectedPlace = {
      Id: place?.Id,
      address: place?.address,
      hotel_name: place?.hotel_name,
      headline: place?.headline,
      NearestIata: place?.NearestIataCode,
      InputType: HotelDestinationInputType.hotel,
      isTravelCitySearch: false,
    };
    this.destinationPlace = selectedPlace;
    this.selectPackagesDestination.emit(selectedPlace);
    this.close();
  }

  public selectPopularPlaceTrigger(place: AutocompletePlace): void {
    const popularPlace = {
      Id: 0,
      Name: place?.description,
      NameFull: `${place?.description}, ${place?.country}`,
      Type: null,
      NearestIata: place?.code,
      InputType: HotelDestinationInputType.location,
      isPopularSelection: true,
    };
    if (this.placeType === PlaceType.ARRIVAL) {
      this.selectPackagesDestination.emit(popularPlace);
    } else {
      this.selectPackagesDeparture.emit(popularPlace);
    }
    this.close();
  }

  public swipePlaces(): void {
    if (this.destinationPlace?.InputType !== HotelDestinationInputType.hotel) {
      this.swipeAction.emit();
    }
  }

  private initKeyEvent(): void {
    this.emitAutocomplete$ = new Subject<string>;
    this.emitAutocomplete$
      .pipe(
        takeUntil(this.subscription$),
        debounceTime(300),
        distinctUntilChanged(),
        tap(value => {
          this.runAutocomplete.emit({ q: value, type: this.placeType });
        })
      ).subscribe();
  }

}
